import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Single-editor reply box with two submit buttons: "Add Comment" (public) and "Add Internal Note" (agents only).
// Submitting as an internal note dynamically rewrites the form action and parameter name before requestSubmit.
// data-controller="support--reply-box"
export default class extends Controller {
  static targets = [
    "commentEditor",
    "suggestBtn",
    "suggestLabel",
    "blankMessage",
  ]
  static values = {
    suggestUrl: String,
    noteUrl: String,
    blankCommentMessage: String,
    blankNoteMessage: String,
  }

  async suggestReply(event) {
    event.preventDefault()
    const url =
      this.suggestUrlValue ||
      event.currentTarget.dataset.supportReplyBoxSuggestUrlValue
    if (!url) return

    if (this.hasSuggestLabelTarget)
      this.suggestLabelTarget.textContent = "Thinking…"
    if (this.hasSuggestBtnTarget) this.suggestBtnTarget.disabled = true

    try {
      const response = await post(url, { responseKind: "json" })
      if (response.ok) {
        const data = await response.json
        if (data.suggestion) {
          this.#insertIntoEditor(data.suggestion)
        }
      } else {
        console.error("[ReplyBox] AI suggest failed:", response.statusCode)
      }
    } catch (err) {
      console.error("[ReplyBox] AI suggest error:", err)
    } finally {
      if (this.hasSuggestLabelTarget)
        this.suggestLabelTarget.textContent = "AI Suggest"
      if (this.hasSuggestBtnTarget) this.suggestBtnTarget.disabled = false
    }
  }

  // Block submitting an empty comment/note (no text and no attachments).
  // Bound to the form's submit event, so it covers both the native "Add Comment"
  // submit and the JS-triggered "Add Internal Note".
  // Says why instead of swallowing the click (CONEK-8039): the server rejects
  // the same empty body with the same message, this just saves the round trip.
  guard(event) {
    if (!this.#isBlank()) return

    event.preventDefault()
    const action = event.target?.getAttribute?.("action") || ""
    this.#showBlankMessage(action.endsWith("/internal_notes"))
  }

  clearBlankMessage() {
    if (this.hasBlankMessageTarget) this.blankMessageTarget.hidden = true
  }

  #showBlankMessage(asNote) {
    if (this.hasBlankMessageTarget) {
      this.blankMessageTarget.textContent = asNote
        ? this.blankNoteMessageValue
        : this.blankCommentMessageValue
      this.blankMessageTarget.hidden = false
    }
    this.element.querySelector("lexxy-editor [contenteditable]")?.focus()
  }

  #isBlank() {
    const form = this.element.querySelector("form")
    const editor = form?.querySelector("lexxy-editor")
    const fileInput = form?.querySelector('input[type="file"]')
    const hasText = editor ? !editor.isEmpty : false
    const hasFiles = fileInput ? fileInput.files.length > 0 : false
    return !hasText && !hasFiles
  }

  submitAsNote(event) {
    event.preventDefault()
    const form = this.element.querySelector("form")
    const editor = form?.querySelector("lexxy-editor")
    if (!form || !editor || !this.noteUrlValue) return

    // Bail before disabling the button so it doesn't get stuck on "Sending...".
    if (this.#isBlank()) {
      this.#showBlankMessage(true)
      return
    }

    const fileInput = form.querySelector(
      'input[type="file"][name="support_ticket_comment[files][]"]',
    )

    // @mention ids (CONEK-7203). The hidden inputs are written by
    // support--mention-tracker under the COMMENT scope, because that is what
    // the form renders as by default; posting as a note has to rename them too,
    // exactly like the editor and the file input below. Without this the ids
    // arrive as support_ticket_comment[mentioned_user_ids][] on a request the
    // notes controller reads as support_internal_note — so the mention is
    // silently dropped and nobody is notified.
    const mentionInputs = [
      ...form.querySelectorAll(
        'input[name="support_ticket_comment[mentioned_user_ids][]"]',
      ),
    ]

    const originalAction = form.action
    const originalName = editor.getAttribute("name")
    const originalFileName = fileInput?.getAttribute("name")

    // Disable the note button while submitting. Turbo's submits-with only
    // covers the real submit button, not this JS-triggered one, so we manage
    // its disabled state manually and restore it when the request settles.
    const button = event.currentTarget
    if (button) {
      button.disabled = true
      button.setAttribute("aria-busy", "true")

      // Mirror the comment button's "Sending..." label (Turbo handles that one
      // automatically; this button is JS-triggered so we swap the text here).
      const sendingText = button.dataset.turboSubmitsWith
      let originalLabel = null
      if (sendingText) {
        originalLabel = button.innerHTML
        button.textContent = sendingText
      }

      form.addEventListener(
        "turbo:submit-end",
        () => {
          button.disabled = false
          button.removeAttribute("aria-busy")
          if (originalLabel !== null) button.innerHTML = originalLabel
        },
        { once: true },
      )
    }

    form.action = this.noteUrlValue
    editor.setAttribute("name", "support_internal_note[content]")
    if (fileInput)
      fileInput.setAttribute("name", "support_internal_note[files][]")
    for (const input of mentionInputs) {
      input.setAttribute("name", "support_internal_note[mentioned_user_ids][]")
    }

    form.requestSubmit()

    // Restore immediately — Turbo captures FormData synchronously on submit event.
    form.action = originalAction
    editor.setAttribute("name", originalName)
    if (fileInput && originalFileName)
      fileInput.setAttribute("name", originalFileName)
    for (const input of mentionInputs) {
      input.setAttribute("name", "support_ticket_comment[mentioned_user_ids][]")
    }
  }

  #insertIntoEditor(text) {
    if (this.hasCommentEditorTarget) {
      const lexxy = this.commentEditorTarget.querySelector("lexxy-editor")
      if (lexxy) {
        const editable = lexxy.querySelector("[contenteditable]")
        if (editable) {
          editable.focus()
          // TODO: execCommand is deprecated — migrate to InputEvent("insertText") or
          // Selection/Range API when Lexxy supports it. For now, execCommand is the
          // most reliable way to insert into contenteditable while preserving undo history.
          document.execCommand("selectAll")
          document.execCommand("insertText", false, text)
          return
        }
      }
    }
    // Fallback: find first contenteditable in the controller element
    const editable = this.element.querySelector(
      "lexxy-editor [contenteditable]",
    )
    if (editable) {
      editable.focus()
      document.execCommand("selectAll")
      document.execCommand("insertText", false, text)
    }
  }
}
